import { AlertCircle, ArrowRight, Check, KeyRound, Minus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaPasswordField } from "@/shared/components/forms/ColocaPasswordField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { AuthShell } from "../components/AuthShell";
import { PasswordStrengthMeter } from "../components/PasswordStrengthMeter";
import { authService } from "../services/authService";
import { scorePassword } from "../utils/passwordStrength";
import { createResetPasswordSchema, type ResetPasswordSchemaTranslator } from "../utils/resetPasswordSchema";

export const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t, activeLanguage } = useTranslations("login");
  const { t: tg } = useTranslations("global");

  const email = searchParams.get("email") ?? "";
  const tokenFromQs = searchParams.get("token") ?? undefined;
  const codeFromState = (location.state as { code?: string } | null)?.code;

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!email || (!tokenFromQs && !codeFromState)) {
      navigate("/auth/forgot-password", { replace: true });
    }
  }, [email, tokenFromQs, codeFromState, navigate]);

  const schemaTranslator: ResetPasswordSchemaTranslator = useMemo(
    () =>
      (key): string => {
        switch (key) {
          case "required":
            return t("fp_new_password_label");
          case "min":
            return t("fp_new_req_length");
          case "number":
            return t("fp_new_req_number");
          case "symbol":
            return t("fp_new_req_symbol");
          case "match":
            return t("fp_new_match_error");
        }
      },
    [t],
  );

  const schema = useMemo(() => createResetPasswordSchema(schemaTranslator), [schemaTranslator]);
  const form = useZodForm(schema, { newPassword: "", confirmPassword: "" }, "onChange");
  const { watch } = form;
  const newPassword = watch("newPassword");
  const confirmPassword = watch("confirmPassword");

  const { score, rules } = scorePassword(newPassword ?? "");
  const passwordsMatch = !!newPassword && newPassword === confirmPassword;

  const strengthLabels = [
    t("fp_new_strength_empty"),
    t("fp_new_strength_weak"),
    t("fp_new_strength_fair"),
    t("fp_new_strength_good"),
    t("fp_new_strength_strong"),
  ] as const;

  const requirements: { label: string; ok: boolean }[] = [
    { label: t("fp_new_req_length"), ok: rules.minLength },
    { label: t("fp_new_req_number"), ok: rules.hasNumber },
    { label: t("fp_new_req_symbol"), ok: rules.hasSymbol },
    { label: t("fp_new_req_long"), ok: rules.longLength },
  ];

  const handleSubmit = async (values: { newPassword: string; confirmPassword: string }) => {
    setError(null);
    setIsLoading(true);
    try {
      await authService.resetPassword({
        email,
        token: tokenFromQs,
        code: tokenFromQs ? undefined : codeFromState,
        newPassword: values.newPassword,
      });
      navigate("/auth/forgot-password/done", { replace: true });
    } catch (err) {
      setError(HttpApiService.getErrorMessage(err) || t("fp_error_network"));
    } finally {
      setIsLoading(false);
    }
  };

  const canSubmit = score === 4 && passwordsMatch && !isLoading;

  return (
    <AuthShell>
      <div className="flex flex-col items-center w-full max-w-md mx-auto">
        <div className="flex flex-col items-center gap-4 mb-8 animate-fade-down">
          <div className="relative">
            <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl animate-pulse"></div>
            <div className="relative bg-primary/10 p-2 rounded-full">
              <KeyRound className="w-8 h-8 text-primary" />
            </div>
          </div>
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold text-foreground">{t("fp_new_title")}</h2>
            <p className="text-sm text-muted-foreground max-w-sm">
              {t("fp_new_subtitle").replace("{email}", email)}
            </p>
          </div>
        </div>

        <ColocaForm
          key={activeLanguage}
          form={form}
          onSubmit={handleSubmit}
          className="w-full space-y-4 animate-fade-up"
        >
          <ColocaPasswordField
            name="newPassword"
            label={t("fp_new_password_label")}
            placeholder={t("fp_new_password_placeholder")}
            showLabel={tg("input_show_password_label")}
            hideLabel={tg("input_hide_password_label")}
          />

          <div className="space-y-1">
            <PasswordStrengthMeter
              value={newPassword ?? ""}
              labels={strengthLabels}
              ariaLabel={t("fp_new_strength")}
            />
            <ul className="space-y-1 pt-2">
              {requirements.map((req) => (
                <li
                  key={req.label}
                  className={cn(
                    "flex items-center gap-2 text-xs transition-colors",
                    req.ok ? "text-primary" : "text-muted-foreground",
                  )}
                >
                  <span
                    className={cn(
                      "grid h-4 w-4 place-items-center rounded-full transition-colors",
                      req.ok ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground",
                    )}
                  >
                    {req.ok ? <Check className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
                  </span>
                  {req.label}
                </li>
              ))}
            </ul>
          </div>

          <ColocaPasswordField
            name="confirmPassword"
            label={t("fp_new_confirm_label")}
            placeholder={t("fp_new_confirm_placeholder")}
            showLabel={tg("input_show_password_label")}
            hideLabel={tg("input_hide_password_label")}
          />
          {confirmPassword && passwordsMatch && (
            <p className="inline-flex items-center gap-1 text-xs font-medium text-primary">
              <Check className="h-3 w-3" />
              {t("fp_new_match_ok")}
            </p>
          )}

          {error && (
            <div className="animate-shake">
              <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />
            </div>
          )}

          <ColocaButton
            type="submit"
            variant="primary"
            size="lg"
            loading={isLoading}
            disabled={!canSubmit}
            className="h-12 w-full text-base font-semibold"
            iconTrailing={ArrowRight}
          >
            {t("fp_new_submit")}
          </ColocaButton>
        </ColocaForm>
      </div>
    </AuthShell>
  );
};

export default ResetPasswordPage;
